import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';
import { Check, ChevronDown } from 'lucide-react';

// Owner pricing 2026-10-16: website $12/yr ($6 every 6 months) after a free
// preview. A domain we register adds its own yearly price, shown before paying.
// Transfers quoted.
const PLANS = [
  {
    name: 'Website',
    price: '$12',
    period: '/year',
    alt: 'or $6 every 6 months',
    description: 'A complete website on your own easypeazy.site address.',
    features: ['Built for you', 'Click to edit', 'Built to be found on Google', 'Secure lock (SSL)', 'Hosting', 'name@easypeazy.site email forwarding'],
    cta: 'Get started',
    to: '/signup',
    recommended: true,
  },
  {
    name: 'Your own domain',
    // Live /api/pricing/domains 2026-10-16: cheapest .de $17/yr; .com $25/yr.
    price: 'From $17',
    period: '/yr',
    alt: '.com example: Website $12 + domain $13',
    description: 'Add a domain we register for you, like yourname.com.',
    features: ['Website $12 + domain = one yearly total', 'You see the exact total before paying', 'Kept renewed for you'],
    cta: 'Get started',
    to: '/signup',
  },
  {
    name: 'Moving a domain you already own',
    price: 'Quote',
    period: '',
    alt: 'Price shown before any charge',
    description: 'Bring your domain over and we keep it renewed and pointed at your site.',
    features: ['We handle the move', 'Kept renewed for you', 'Your address stays the same'],
    cta: 'Ask us',
    href: 'mailto:lincoln@unitedundergod.org',
  },
];

const FAQS = [
  { q: 'Is there a monthly fee?', a: 'No. You pay once a year, or every 6 months if you prefer. There are no monthly bills.' },
  { q: 'Can I use my own domain?', a: "Yes. Your own domain: + its yearly price, shown before you pay. For a .com, that's Website $12 + domain $13 = $25/yr." },
  { q: 'Can I edit it myself?', a: 'Yes. Click any wording or photo on your site to change it, anytime, from a simple dashboard.' },
  { q: 'What if I already have a domain?', a: 'You can connect it to your site at the $12 a year Website price, or move it over to us for a quoted price shown before any charge.' },
];

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
        {q}
        <ChevronDown className={`w-5 h-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-5 text-base text-slate-400 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-slate-950 overflow-x-hidden" data-testid="pricing-page">
      <SiteNav />

      <section className="pt-28 pb-12 md:pt-32 md:pb-16 px-6 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-transparent pointer-events-none" />
        <h1 className="relative text-4xl md:text-6xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>A real website for $12 a year.</h1>
        <p className="relative text-lg md:text-xl text-slate-400">No monthly bills. Edit it yourself anytime.</p>
      </section>

      <section className="px-6 py-12 md:py-16">
        <div className="max-w-6xl mx-auto grid gap-6 md:grid-cols-3">
          {PLANS.map((p) => (
            <div key={p.name}
              className={`relative flex flex-col rounded-2xl p-8 bg-slate-900/60 ${p.recommended ? 'border-2 border-indigo-500 shadow-xl shadow-indigo-500/10' : 'border border-slate-800'}`}>
              {p.recommended && <span className="absolute -top-3 left-8 rounded-full bg-indigo-500 px-3 py-1 text-xs font-semibold text-white">Most popular</span>}
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{p.name}</h2>
              <p className="mt-4 flex items-baseline gap-1">
                <span className="font-bold text-white" style={{ fontFamily: 'Outfit', fontSize: 48, lineHeight: 1.1 }}>{p.price}</span>
                {p.period && <span className="text-base text-slate-500">{p.period}</span>}
              </p>
              <p className="mt-1 text-sm text-slate-500">{p.alt}</p>
              <p className="mt-3 text-slate-400">{p.description}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-slate-300"><Check className="w-5 h-5 mt-0.5 shrink-0 text-indigo-400" />{f}</li>
                ))}
              </ul>
              {React.createElement(p.href ? 'a' : Link, { ...(p.href ? { href: p.href } : { to: p.to }),
                className: `mt-8 flex h-12 items-center justify-center rounded-lg font-semibold transition-colors ${p.recommended ? 'bg-indigo-500 hover:bg-indigo-600 text-white' : 'border border-slate-700 text-slate-100 hover:bg-slate-800'}` }, p.cta)}
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-slate-400">Email forwarding is included with every site. We don't offer separate mailboxes.</p>
      </section>

      <section className="px-6 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-semibold text-white text-center mb-8" style={{ fontFamily: 'Outfit' }}>Questions</h2>
          {FAQS.map((f) => <Faq key={f.q} {...f} />)}
        </div>
      </section>

      <section className="px-6 pt-12 pb-24 md:pt-16 md:pb-24 text-center border-t border-slate-800 bg-indigo-500/5">
        <h2 className="text-3xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Get your website today.</h2>
        <Link to="/signup" className="inline-flex h-12 items-center justify-center rounded-lg bg-indigo-500 hover:bg-indigo-600 px-8 font-semibold text-white shadow-lg shadow-indigo-500/20">Get started</Link>
      </section>

      <SiteFooter />
    </div>
  );
}
